import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import config from '../../utils/config';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { toast } from 'sonner';
import { Workspace as WorkspaceType, updateWorkspaceGeoRestrictions, previewWorkspaceDateUpdate, updateWorkspaceDates } from '../../utils/sqrone-api';
import { DomainBrand } from '../../utils/branding-api';
import { BrandImage } from './BrandImage';
import { DateOverrideDialog } from './DateOverrideDialog';
import { Badge } from '../../ui/badge';
import { X, Globe, MapPin, CalendarClock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Calendar } from '../../ui/calendar';
import { format } from 'date-fns';

interface WorkspaceProps {
  mode: 'create' | 'edit';
  workspace?: WorkspaceType;
  domains: DomainBrand[]
  onSuccess: () => void;
  onCancel: () => void;
}

export function Workspace({ mode, workspace, domains, onSuccess, onCancel }: WorkspaceProps) {
  const { token, isAuthenticated } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [domainBrandId, setDomainBrandId] = useState<number | null>(null);
  const [brandImageFile, setBrandImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);

  // FR-24: Geo-restriction fields
  const [deactivatedRedirectUrl, setDeactivatedRedirectUrl] = useState('');
  const [allowedCountries, setAllowedCountries] = useState<string[]>([]);
  const [allowedRegions, setAllowedRegions] = useState<string[]>([]);
  const [geoBlockBehavior, setGeoBlockBehavior] = useState<string>('404');
  const [countryInput, setCountryInput] = useState('');
  const [regionInput, setRegionInput] = useState('');

  // FR-32/FR-33: Workspace date fields
  const [expiresAt, setExpiresAt] = useState<Date | undefined>(undefined);
  const [startsAt, setStartsAt] = useState<Date | undefined>(undefined);
  const [expiresAtPickerOpen, setExpiresAtPickerOpen] = useState(false);
  const [startsAtPickerOpen, setStartsAtPickerOpen] = useState(false);

  // FR-32/FR-33: Date override confirmation dialog
  const [showDateOverrideDialog, setShowDateOverrideDialog] = useState(false);
  const [dateOverridePreview, setDateOverridePreview] = useState<{
    urlsWithCustomExpiry: number;
    urlsWithCustomStart: number;
    totalUrls: number;
  } | null>(null);
  const [pendingDateUpdate, setPendingDateUpdate] = useState<{
    expiresAt: string | null;
    startsAt: string | null;
  } | null>(null);

  const baseUrl = config.apiBaseUrl;

  useEffect(() => {
    if (mode === 'edit' && workspace) {
      setName(workspace.name);
      setDescription(workspace.description || '');
      setTags(workspace.tags || '');
      setDomainBrandId(workspace.domainBrandId);

      // FR-24: Load geo-restriction data
      setDeactivatedRedirectUrl(workspace.deactivatedRedirectUrl || '');
      setGeoBlockBehavior(workspace.geoBlockBehavior || '404');

      // Parse JSON arrays
      if (workspace.allowedCountries) {
        try {
          const countries = JSON.parse(workspace.allowedCountries);
          setAllowedCountries(Array.isArray(countries) ? countries : []);
        } catch (e) {
          setAllowedCountries([]);
        }
      }

      if (workspace.allowedRegions) {
        try {
          const regions = JSON.parse(workspace.allowedRegions);
          setAllowedRegions(Array.isArray(regions) ? regions : []);
        } catch (e) {
          setAllowedRegions([]);
        }
      }

      // FR-32/FR-33: Load workspace dates
      if (workspace.expiresAt) {
        setExpiresAt(new Date(workspace.expiresAt));
      }
      if (workspace.startsAt) {
        setStartsAt(new Date(workspace.startsAt));
      }
    }
  }, [mode, workspace]);

  useEffect(() => {
    //fetchDomains();
  }, []);

  const handleImageAdded = (file: File | null) => {
    setBrandImageFile(file);
  };

  // FR-24: Geo-restriction helpers
  const handleAddCountry = () => {
    const trimmed = countryInput.trim().toUpperCase();
    if (trimmed && !allowedCountries.includes(trimmed)) {
      setAllowedCountries([...allowedCountries, trimmed]);
      setCountryInput('');
    }
  };

  const handleRemoveCountry = (country: string) => {
    setAllowedCountries(allowedCountries.filter(c => c !== country));
  };

  const handleAddRegion = () => {
    const trimmed = regionInput.trim();
    if (trimmed && !allowedRegions.includes(trimmed)) {
      setAllowedRegions([...allowedRegions, trimmed]);
      setRegionInput('');
    }
  };

  const handleRemoveRegion = (region: string) => {
    setAllowedRegions(allowedRegions.filter(r => r !== region));
  };

  const uploadLogo = async (workspaceId: number): Promise<void> => {
    if (!brandImageFile || !token) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', brandImageFile);

      const response = await fetch(`${baseUrl}/api/workspaces/${workspaceId}/branding`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Logo upload failed:', response.status, errorText);
        throw new Error('Failed to upload logo');
      }

      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
      throw error;
    }
  };

  // FR-32/FR-33: Helper to check if dates have changed
  const datesHaveChanged = () => {
    if (mode !== 'edit' || !workspace) return false;

    const oldExpiresAt = workspace.expiresAt ? new Date(workspace.expiresAt).toISOString() : null;
    const newExpiresAt = expiresAt ? expiresAt.toISOString() : null;

    const oldStartsAt = workspace.startsAt ? new Date(workspace.startsAt).toISOString() : null;
    const newStartsAt = startsAt ? startsAt.toISOString() : null;

    return oldExpiresAt !== newExpiresAt || oldStartsAt !== newStartsAt;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Please enter a workspace name');
      return;
    }

    if (!token) {
      toast.error('You must be logged in');
      return;
    }

    // FR-32/FR-33: In edit mode, check for custom dates before updating
    if (mode === 'edit' && workspace && datesHaveChanged()) {
      try {
        const preview = await previewWorkspaceDateUpdate(workspace.id, token);

        if (preview.urlsWithCustomExpiry > 0 || preview.urlsWithCustomStart > 0) {
          // Store pending update and show confirmation dialog
          setPendingDateUpdate({
            expiresAt: expiresAt ? expiresAt.toISOString() : null,
            startsAt: startsAt ? startsAt.toISOString() : null,
          });
          setDateOverridePreview(preview);
          setShowDateOverrideDialog(true);
          return; // Don't proceed with update yet
        }
      } catch (error) {
        console.error('Failed to preview date update:', error);
        // Continue with update even if preview fails
      }
    }

    // Proceed with normal update
    await performUpdate(false);
  };

  // FR-32/FR-33: Perform the actual update with optional date propagation
  const performUpdate = async (overrideCustomDates: boolean) => {
    if (!token) {
      toast.error('You must be logged in');
      return;
    }

    setLoading(true);

    try {
      const url = mode === 'create'
        ? `${baseUrl}/api/workspaces`
        : `${baseUrl}/api/workspaces/${workspace?.id}`;

      const method = mode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          tags: tags.trim() || null,
          domainBrandId: domainBrandId,
          expiresAt: expiresAt ? expiresAt.toISOString() : null,  // FR-32
          startsAt: startsAt ? startsAt.toISOString() : null,  // FR-33
        })
      });

      if (response.ok) {
        const savedWorkspace = await response.json();

        // Upload logo if an image was selected
        if (brandImageFile) {
          try {
            await uploadLogo(savedWorkspace.id);
          } catch (logoError) {
            // Workspace was saved but logo upload failed - still show success but with a warning
            console.error('Logo upload failed, but workspace was saved:', logoError);
          }
        }

        // FR-24: Update geo-restrictions if any are set
        if (allowedCountries.length > 0 || allowedRegions.length > 0 || deactivatedRedirectUrl.trim()) {
          try {
            await updateWorkspaceGeoRestrictions(
              savedWorkspace.id,
              {
                allowedCountries: allowedCountries.length > 0 ? allowedCountries : null,
                allowedRegions: allowedRegions.length > 0 ? allowedRegions : null,
                geoBlockBehavior: geoBlockBehavior,
                deactivatedRedirectUrl: deactivatedRedirectUrl.trim() || null,
              },
              token
            );
          } catch (geoError) {
            console.error('Geo-restrictions update failed:', geoError);
            toast.warning('Workspace saved, but geo-restrictions update failed');
          }
        }

        // FR-32/FR-33: Propagate dates to child URLs when dates have changed
        if (mode === 'edit' && datesHaveChanged()) {
          try {
            const datesToPropagate = pendingDateUpdate || {
              expiresAt: expiresAt ? expiresAt.toISOString() : null,
              startsAt: startsAt ? startsAt.toISOString() : null,
            };

            await updateWorkspaceDates(
              savedWorkspace.id,
              datesToPropagate.expiresAt,
              datesToPropagate.startsAt,
              overrideCustomDates,
              token
            );
          } catch (dateError) {
            console.error('Date propagation failed:', dateError);
            toast.warning('Workspace saved, but date propagation failed');
          }
        }

        toast.success(mode === 'create' ? 'Workspace created successfully!' : 'Workspace updated successfully!');

        // Clear pending date update
        setPendingDateUpdate(null);
        setDateOverridePreview(null);

        onSuccess();
      } else {
        const errorText = await response.text();
        console.error(`${mode} workspace failed:`, response.status, errorText);
        try {
          const error = JSON.parse(errorText);
          toast.error(error.message || `Failed to ${mode} workspace`);
        } catch {
          toast.error(`Error ${response.status}: Failed to ${mode} workspace`);
        }
      }
    } catch (error) {
      console.error(`Error ${mode}ing workspace:`, error);
      toast.error(`Failed to ${mode} workspace`);
    } finally {
      setLoading(false);
      setShowDateOverrideDialog(false);
    }
  };

  // FR-32/FR-33: Handle date override dialog confirmation
  const handleDateOverrideConfirm = async (overrideCustomDates: boolean) => {
    await performUpdate(overrideCustomDates);
  };

  if (!isAuthenticated || !token) {
    return (
    <div className="workspaces-container">
        <div className="no-selection">
        <p>Please sign in to manage workspaces</p>
        </div>
    </div>
    );
 }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Accordion type="multiple" defaultValue={["basic"]} className="w-full">
        {/* Basic Information Section */}
        <AccordionItem value="basic">
          <AccordionTrigger className="text-base font-semibold">
            Basic Information
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="name">Workspace Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Marketing Links"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Describe what this workspace is for..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={loading}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tags">Tags (optional)</Label>
                <Input
                  id="tags"
                  placeholder="e.g., marketing, social, workspace"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground">
                  Separate tags with commas
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Branding & Domain Section */}
        <AccordionItem value="branding">
          <AccordionTrigger className="text-base font-semibold">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span>Branding & Domain</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <BrandImage
                newCollectionDomain={domainBrandId ? domains.find(d => d.id === domainBrandId)?.domain || '' : ''}
                accountBrandImage={ workspace?.accountBranding ? workspace.accountBranding : null}
                onImageAdded={handleImageAdded}
              />

              <div className="space-y-2">
                <Label htmlFor="domain">Domain (optional)</Label>
                <Select
                  value={domainBrandId?.toString() || "none"}
                  onValueChange={(value) => setDomainBrandId(value === "none" ? null : parseInt(value))}
                  disabled={loading || loadingDomains}
                >
                  <SelectTrigger id="domain">
                    <SelectValue placeholder={loadingDomains ? "Loading domains..." : "Select a domain"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No domain (use default)</SelectItem>
                    {domains.map((domain) => (
                      <SelectItem key={domain.id} value={domain.id.toString()}>
                        <div className="flex items-center gap-2">
                          <span>{domain.domain}</span>
                          {domain.verified && (
                            <span className="text-xs text-green-600">✓ Verified</span>
                          )}
                          {!domain.verified && (
                            <span className="text-xs text-yellow-600">⚠ Pending</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Choose a custom domain for short URLs in this workspace
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Geo-Targeting Section */}
        <AccordionItem value="geo">
          <AccordionTrigger className="text-base font-semibold">
            <div className="flex items-center gap-2 flex-1">
              <MapPin className="h-4 w-4" />
              <span>Geo-Targeting</span>
              {(allowedCountries.length > 0 || allowedRegions.length > 0) ? (
                <Badge variant="secondary" className="ml-auto mr-2">
                  {allowedCountries.length + allowedRegions.length} restrictions
                </Badge>
              ) : (
                <Badge variant="outline" className="ml-auto mr-2">Optional</Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Restrict access to this workspace's links based on visitor location. Both country AND region must match.
              </p>

              <div className="space-y-2">
                <Label htmlFor="countries">Allowed Countries</Label>
                <div className="flex gap-2">
                  <Input
                    id="countries"
                    placeholder="e.g., US, HU, GB (2-letter codes)"
                    value={countryInput}
                    onChange={(e) => setCountryInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCountry())}
                    disabled={loading}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddCountry}
                    disabled={!countryInput.trim() || loading}
                  >
                    Add
                  </Button>
                </div>
                {allowedCountries.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {allowedCountries.map((country) => (
                      <Badge key={country} variant="secondary" className="gap-1">
                        {country}
                        <button
                          type="button"
                          onClick={() => handleRemoveCountry(country)}
                          className="ml-1 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                        >
                          <X className="size-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Use ISO 3166-1 alpha-2 country codes (e.g., US, HU, GB, DE)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="regions">Allowed Regions/Cities</Label>
                <div className="flex gap-2">
                  <Input
                    id="regions"
                    placeholder="e.g., Budapest, California, London"
                    value={regionInput}
                    onChange={(e) => setRegionInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRegion())}
                    disabled={loading}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddRegion}
                    disabled={!regionInput.trim() || loading}
                  >
                    Add
                  </Button>
                </div>
                {allowedRegions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {allowedRegions.map((region) => (
                      <Badge key={region} variant="secondary" className="gap-1">
                        {region}
                        <button
                          type="button"
                          onClick={() => handleRemoveRegion(region)}
                          className="ml-1 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                        >
                          <X className="size-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  City or state/province names as detected by geolocation service
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="geoBlockBehavior">Geo-Block Behavior</Label>
                <Select
                  value={geoBlockBehavior}
                  onValueChange={setGeoBlockBehavior}
                  disabled={loading}
                >
                  <SelectTrigger id="geoBlockBehavior">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="404">Return 404 Not Found</SelectItem>
                    <SelectItem value="Redirect">Redirect to URL below</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  What happens when a visitor's location doesn't match
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deactivatedRedirectUrl">Deactivated/Blocked Redirect URL (optional)</Label>
                <Input
                  id="deactivatedRedirectUrl"
                  type="url"
                  placeholder="https://example.com/blocked"
                  value={deactivatedRedirectUrl}
                  onChange={(e) => setDeactivatedRedirectUrl(e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground">
                  Redirect users here when links are deactivated or geo-blocked (if "Redirect" is selected)
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* FR-32/FR-33: Schedule Section */}
        <AccordionItem value="schedule">
          <AccordionTrigger className="text-base font-semibold">
            <div className="flex items-center gap-2 flex-1">
              <CalendarClock className="h-4 w-4" />
              <span>Schedule</span>
              {(expiresAt || startsAt) ? (
                <Badge variant="secondary" className="ml-auto mr-2">
                  {[startsAt && 'Start', expiresAt && 'Expiry'].filter(Boolean).join(' & ')}
                </Badge>
              ) : (
                <Badge variant="outline" className="ml-auto mr-2">Optional</Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Set start and expiry dates for this workspace. All URLs in this workspace will inherit these dates unless overridden individually.
              </p>

              {/* Start Date */}
              <div className="space-y-2">
                <Label>Start Date (optional)</Label>
                <Popover open={startsAtPickerOpen} onOpenChange={setStartsAtPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      disabled={loading}
                    >
                      <CalendarClock className="mr-2 h-4 w-4" />
                      {startsAt ? format(startsAt, 'PPP') : <span>No start date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startsAt}
                      onSelect={(date) => {
                        setStartsAt(date);
                        setStartsAtPickerOpen(false);
                      }}
                      initialFocus
                    />
                    {startsAt && (
                      <div className="p-3 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            setStartsAt(undefined);
                            setStartsAtPickerOpen(false);
                          }}
                        >
                          Clear date
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  URLs accessed before this date will show "not yet available"
                </p>
              </div>

              {/* Expiry Date */}
              <div className="space-y-2">
                <Label>Expiry Date (optional)</Label>
                <Popover open={expiresAtPickerOpen} onOpenChange={setExpiresAtPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      disabled={loading}
                    >
                      <CalendarClock className="mr-2 h-4 w-4" />
                      {expiresAt ? format(expiresAt, 'PPP') : <span>No expiry date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={expiresAt}
                      onSelect={(date) => {
                        setExpiresAt(date);
                        setExpiresAtPickerOpen(false);
                      }}
                      initialFocus
                    />
                    {expiresAt && (
                      <div className="p-3 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            setExpiresAt(undefined);
                            setExpiresAtPickerOpen(false);
                          }}
                        >
                          Clear date
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  URLs will expire and become unavailable after this date
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="flex-1"
        >
          {loading ? 'Saving...' : mode === 'create' ? 'Create Workspace' : 'Save Changes'}
        </Button>
      </div>

      {/* FR-32/FR-33: Date override confirmation dialog */}
      {dateOverridePreview && (
        <DateOverrideDialog
          open={showDateOverrideDialog}
          onOpenChange={setShowDateOverrideDialog}
          urlsWithCustomExpiry={dateOverridePreview.urlsWithCustomExpiry}
          urlsWithCustomStart={dateOverridePreview.urlsWithCustomStart}
          totalUrls={dateOverridePreview.totalUrls}
          onConfirm={handleDateOverrideConfirm}
          loading={loading}
        />
      )}
    </form>
  );
}
